import { create } from 'zustand'
import { api } from '../lib/api'
import { quotaGateFromError, quotaGateFromReason, type QuotaGateState } from '../lib/quota'
import type { LoadState } from '../hooks/useAsyncData'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Competency {
  name: string
  importance: string
  coverage: string
  notes?: string
}

export interface GapItem {
  competency: string
  severity: string
  suggestion?: string
}

export interface GapAnalysis {
  competencies_required: Competency[]
  gap_list: GapItem[]
  domain_transfer_opportunities: Array<{
    accomplishment_id: string
    target_competency: string
    rationale: string
  }>
}

export interface InterrogationQuestion {
  id: string
  category: string
  text: string
  context?: string
  conflict_sources?: Array<{ label: string; text: string }>
}

export interface QAEntry {
  question_id: string
  question: string
  category: string
  answer: string | null
  skipped: boolean
}

export interface CVSection {
  type: string
  content: Record<string, unknown>
}

export interface CVDocument {
  user: {
    name: string
    email: string
    phone?: string
    linkedin_url?: string
    location?: string
    education: Array<{ degree?: string; institution: string; year?: string | number | null }>
    languages: string[]
  }
  sections: CVSection[]
}

export interface AssemblyProgress {
  stage: string
  message: string
  pct: number | null
}

interface SessionState {
  gap_analysis?: GapAnalysis
  current_question?: InterrogationQuestion
  qa_history?: QAEntry[]
  cv_document?: CVDocument
  cv_html?: string
  interrogation_complete: boolean
}

export interface CVSession {
  session_id: string
  current_step: string
  status: string
  question_count: number
  // Soft ceiling on interrogation questions, for "Question N · up to M" progress.
  max_questions?: number
  state: SessionState
}

/**
 * What to re-run after the user fixes an invalid BYO key in place, so they land
 * back where they were. Analysis carries the JD the store still holds (a fresh
 * /start with the same JD loses nothing — analysis fails before any Q&A exists);
 * assembly re-runs from the server-persisted interrogation answers. A null
 * context falls back to reloading the current session.
 */
type ResumeContext =
  | { kind: 'analysis'; jdText: string; jobTitle?: string; company?: string; notes?: string }
  | { kind: 'assembly' }

interface CVGenerationState {
  session: CVSession | null
  loadState: LoadState
  error: string | null
  // Set when a flow fails because a usage cap was hit (per-user or global) or a
  // BYO key was rejected. The page renders <QuotaGate> from this.
  quotaGate: QuotaGateState | null
  // Set alongside a byo_key_invalid gate; consumed by resumeAfterKeyFix.
  resumeContext: ResumeContext | null
  approvalResult: {
    cv_record_id: string
    docx_available: boolean
    pdf_available: boolean
    export_failed: boolean
  } | null
  assemblyProgress: AssemblyProgress | null

  startGeneration: (
    jdText: string,
    jobTitle?: string,
    company?: string,
    notes?: string
  ) => Promise<void>
  fetchActiveSession: () => Promise<void>
  fetchSession: (sessionId: string) => Promise<void>
  resumeAssemblyIfNeeded: () => Promise<void>
  resumeAfterKeyFix: () => Promise<void>
  respond: (params: { questionId: string; answer: string | null; skipped: boolean }) => Promise<void>
  skipAndAssemble: () => Promise<void>
  assemble: () => Promise<void>
  enterEditing: () => Promise<void>
  saveDraft: (content: CVDocument) => Promise<void>
  saveDraftHtml: (html: string) => Promise<void>
  approve: (content?: CVDocument, html?: string, docxB64?: string) => Promise<void>
  revertToSnapshot: () => Promise<CVDocument | null>
  clearError: () => void
  abandonSession: () => Promise<void>
  reset: () => void
}

interface ApiResponse<T> {
  data: T
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Set the error state from a thrown value, routing a QUOTA_EXCEEDED 402 to the
 * gate (no generic error message) and everything else to the message string.
 * This is the single place the synchronous transport is mapped — components
 * never inspect the error code themselves.
 */
function _failFrom(
  err: unknown,
  set: (partial: Partial<CVGenerationState>) => void,
  extra: Partial<CVGenerationState> = {},
  resumeCtx?: ResumeContext,
): void {
  const gate = quotaGateFromError(err)
  set({
    error: gate ? null : (err as Error).message,
    quotaGate: gate,
    // Only stamp a resume context when this is the invalid-key gate.
    resumeContext: gate?.kind === 'byo_key_invalid' ? (resumeCtx ?? null) : null,
    loadState: 'error',
    ...extra,
  })
}

async function _pollAssemblyCompletion(
  sessionId: string,
  set: (partial: Partial<CVGenerationState>) => void,
): Promise<void> {
  const TIMEOUT_MS = 5 * 60 * 1000
  const pollStart = Date.now()
  while (Date.now() - pollStart < TIMEOUT_MS) {
    await new Promise<void>(resolve => setTimeout(resolve, 3000))
    const statusRes = await api.get<ApiResponse<{
      status: string
      cv_record_id: string | null
      error: string | null
      error_detail: string | null
      error_reason: string | null
      details: { spent: number | null; cap: number | null; source: string } | null
      progress: AssemblyProgress | null
    }>>(
      `/api/v1/cv/${sessionId}/assemble/status`
    )
    const { status, error, progress } = statusRes.data
    if (progress) set({ assemblyProgress: progress })
    if (status === 'complete') {
      const fullRes = await api.get<ApiResponse<CVSession>>(`/api/v1/cv/session/${sessionId}`)
      set({ session: fullRes.data, loadState: 'ready', assemblyProgress: null })
      return
    }
    if (status === 'failed') {
      const gate = quotaGateFromReason(statusRes.data)
      if (gate) {
        set({
          quotaGate: gate,
          error: null,
          loadState: 'error',
          assemblyProgress: null,
          resumeContext: gate.kind === 'byo_key_invalid' ? { kind: 'assembly' } : null,
        })
        return
      }
      throw new Error(error ?? 'Assembly failed')
    }
  }
  throw new Error('Assembly timed out after 5 minutes')
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useCVGenerationStore = create<CVGenerationState>((set, get) => ({
  session: null,
  loadState: 'idle',
  error: null,
  quotaGate: null,
  resumeContext: null,
  approvalResult: null,
  assemblyProgress: null,

  startGeneration: async (jdText, jobTitle, company, notes) => {
    set({ loadState: 'loading', error: null, quotaGate: null, approvalResult: null })
    try {
      const res = await api.post<ApiResponse<{ status: string; session_id: string }>>('/api/v1/cv/start', {
        jd_text: jdText,
        job_title: jobTitle || null,
        company: company || null,
        notes: notes || null,
      })
      const sessionId = res.data.session_id

      // Show analysis step while polling
      set({
        session: {
          session_id: sessionId,
          current_step: 'cv.analysis',
          status: 'in_progress',
          question_count: 0,
          state: { interrogation_complete: false },
        },
      })

      const pollStart = Date.now()
      const TIMEOUT_MS = 5 * 60 * 1000
      while (Date.now() - pollStart < TIMEOUT_MS) {
        await new Promise<void>(resolve => setTimeout(resolve, 3000))
        const statusRes = await api.get<ApiResponse<{
          status: string
          error: string | null
          error_reason: string | null
          details: { spent: number | null; cap: number | null; source: string } | null
        }>>(
          `/api/v1/cv/${sessionId}/start/status`
        )
        if (statusRes.data.status === 'complete') {
          const fullRes = await api.get<ApiResponse<CVSession>>(`/api/v1/cv/session/${sessionId}`)
          set({ session: fullRes.data, loadState: 'ready' })
          return
        }
        if (statusRes.data.status === 'failed') {
          const gate = quotaGateFromReason(statusRes.data)
          if (gate) {
            set({
              quotaGate: gate,
              error: null,
              loadState: 'error',
              resumeContext:
                gate.kind === 'byo_key_invalid'
                  ? { kind: 'analysis', jdText, jobTitle, company, notes }
                  : null,
            })
            return
          }
          throw new Error(statusRes.data.error ?? 'Analysis failed')
        }
      }
      throw new Error('Analysis timed out after 5 minutes')
    } catch (err) {
      _failFrom(err, set, {}, { kind: 'analysis', jdText, jobTitle, company, notes })
    }
  },

  fetchActiveSession: async () => {
    set({ loadState: 'loading', error: null, quotaGate: null })
    try {
      const res = await api.get<ApiResponse<{ session: CVSession | null }>>(
        '/api/v1/cv/active-session'
      )
      if (res.data.session) {
        set({ session: res.data.session, loadState: 'ready' })
      } else {
        set({ loadState: 'ready' })
      }
    } catch {
      set({ loadState: 'error' })
    }
  },

  fetchSession: async (sessionId: string) => {
    set({ loadState: 'loading', error: null, quotaGate: null })
    try {
      const res = await api.get<ApiResponse<CVSession>>(
        `/api/v1/cv/session/${sessionId}`
      )
      set({ session: res.data, loadState: 'ready' })
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
    }
  },

  resumeAssemblyIfNeeded: async () => {
    const session = get().session
    if (!session || session.state.cv_document) return
    set({ loadState: 'loading', error: null, quotaGate: null, assemblyProgress: null })
    try {
      const statusRes = await api.get<ApiResponse<{
        status: string
        cv_record_id: string | null
        error: string | null
        error_detail: string | null
        error_reason: string | null
        details: { spent: number | null; cap: number | null; source: string } | null
        progress: AssemblyProgress | null
      }>>(`/api/v1/cv/${session.session_id}/assemble/status`)
      const { status, error, progress } = statusRes.data
      if (progress) set({ assemblyProgress: progress })
      if (status === 'complete') {
        const fullRes = await api.get<ApiResponse<CVSession>>(`/api/v1/cv/session/${session.session_id}`)
        set({ session: fullRes.data, loadState: 'ready', assemblyProgress: null })
        return
      }
      if (status === 'failed') {
        const gate = quotaGateFromReason(statusRes.data)
        set({
          quotaGate: gate,
          error: gate ? null : (error ?? 'Assembly failed'),
          loadState: 'error',
          assemblyProgress: null,
          resumeContext: gate?.kind === 'byo_key_invalid' ? { kind: 'assembly' } : null,
        })
        return
      }
      // 'queued' or 'running' — poll without re-queuing
      await _pollAssemblyCompletion(session.session_id, set)
    } catch (err) {
      _failFrom(err, set, { assemblyProgress: null }, { kind: 'assembly' })
    }
  },

  resumeAfterKeyFix: async () => {
    // Called after the user saves a fresh key in the invalid-key gate. Re-run
    // the step that stalled, reusing work: analysis re-submits the held JD;
    // assembly re-runs from persisted answers; otherwise reload the session so
    // the user is back on their step to retry the one action.
    const ctx = get().resumeContext
    set({ quotaGate: null, resumeContext: null, error: null })
    if (ctx?.kind === 'analysis') {
      await get().startGeneration(ctx.jdText, ctx.jobTitle, ctx.company, ctx.notes)
      return
    }
    if (ctx?.kind === 'assembly') {
      await get().assemble()
      return
    }
    const session = get().session
    if (session) await get().fetchSession(session.session_id)
    else await get().fetchActiveSession()
  },

  respond: async ({ questionId, answer, skipped }) => {
    const session = get().session
    if (!session) return
    set({ loadState: 'loading', error: null, quotaGate: null })
    try {
      const res = await api.post<ApiResponse<CVSession>>('/api/v1/cv/respond', {
        session_id: session.session_id,
        question_id: questionId,
        answer,
        skipped,
      })
      set({ session: res.data, loadState: 'ready' })
    } catch (err) {
      _failFrom(err, set)
    }
  },

  skipAndAssemble: async () => {
    const session = get().session
    if (!session) return
    set({ loadState: 'loading', error: null, quotaGate: null, assemblyProgress: null })
    try {
      const res = await api.post<ApiResponse<CVSession>>('/api/v1/cv/skip-and-assemble', {
        session_id: session.session_id,
      })
      set({ session: res.data })
      await _pollAssemblyCompletion(session.session_id, set)
    } catch (err) {
      _failFrom(err, set, { assemblyProgress: null }, { kind: 'assembly' })
    }
  },

  assemble: async () => {
    const session = get().session
    if (!session) return
    set({ loadState: 'loading', error: null, quotaGate: null, assemblyProgress: null })
    try {
      await api.post('/api/v1/cv/assemble', { session_id: session.session_id })
      set({ session: { ...session, current_step: 'cv.assembly', status: 'in_progress' } })
      await _pollAssemblyCompletion(session.session_id, set)
    } catch (err) {
      _failFrom(err, set, { assemblyProgress: null }, { kind: 'assembly' })
    }
  },

  // Telemetry only: marks the session as entering an editing round so
  // cv_records.iteration_count is incremented server-side. Fired from the
  // editor's first change of a round, so it must not touch loadState, session,
  // or error — those drive the Approve and Revert buttons, a loading render and
  // an error branch, and mutating them here would disable the user's controls
  // mid-edit, swap the session object under the open editor, or throw the step
  // into an error state because a telemetry POST failed. Failures are swallowed
  // for the same reason: losing a count must never cost the user their edit.
  enterEditing: async () => {
    const session = get().session
    if (!session) return
    try {
      await api.post<ApiResponse<CVSession>>('/api/v1/cv/edit', {
        session_id: session.session_id,
      })
    } catch {
      // Intentionally ignored — see above.
    }
  },

  saveDraft: async (content) => {
    const session = get().session
    if (!session) return
    try {
      await api.post('/api/v1/cv/save-draft', {
        session_id: session.session_id,
        assembled_content: content,
      })
      set({
        session: {
          ...session,
          state: { ...session.state, cv_document: content },
        },
      })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  saveDraftHtml: async (html) => {
    const session = get().session
    if (!session) return
    // Let a save failure REJECT so the caller (ReviewStep autosave) can retry and
    // show its inline "Failed to save" status. We deliberately do not set the
    // global `error` here: that field drives the page-level blocking banner
    // (assembly/analysis failures); a transient autosave failure must not lie with
    // a green "Saved", nor hijack the whole page. The session-state update only
    // runs after the request resolves.
    await api.post('/api/v1/cv/save-draft', {
      session_id: session.session_id,
      assembled_html: html,
    })
    set({
      session: {
        ...session,
        state: { ...session.state, cv_html: html },
      },
    })
  },

  approve: async (content, html, docxB64) => {
    const session = get().session
    if (!session) return
    set({ loadState: 'loading', error: null })
    try {
      const res = await api.post<
        ApiResponse<{
          cv_record_id: string
          approved: boolean
          docx_available: boolean
          pdf_available: boolean
          export_failed: boolean
        }>
      >('/api/v1/cv/approve', {
        session_id: session.session_id,
        assembled_content: content || null,
        assembled_html: html || null,
        assembled_docx_b64: docxB64 || null,
      })
      set({
        approvalResult: {
          cv_record_id: res.data.cv_record_id,
          docx_available: res.data.docx_available,
          pdf_available: res.data.pdf_available,
          export_failed: res.data.export_failed,
        },
        session: {
          ...session,
          current_step: 'cv.approved',
          status: 'completed',
        },
        loadState: 'ready',
      })
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
    }
  },

  revertToSnapshot: async () => {
    const session = get().session
    if (!session) return null
    set({ loadState: 'loading', error: null })
    try {
      const res = await api.post<ApiResponse<{ cv_document: CVDocument }>>(
        '/api/v1/cv/revert-to-snapshot',
        { session_id: session.session_id }
      )
      const snapshot = res.data.cv_document
      set({
        session: {
          ...session,
          state: {
            ...session.state,
            cv_document: snapshot,
            cv_html: undefined,
          },
        },
        loadState: 'ready',
      })
      return snapshot
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
      return null
    }
  },

  clearError: () => set({ error: null, quotaGate: null, resumeContext: null }),
  abandonSession: async () => {
    const session = get().session
    if (session) {
      try {
        await api.post(`/api/v1/cv/${session.session_id}/abandon`, {})
      } catch {
        // Even if the server call fails, drop the local session so the user is
        // never trapped on a broken flow — the orphaned row is harmless.
      }
    }
    set({ session: null, loadState: 'idle', error: null, quotaGate: null, resumeContext: null, approvalResult: null, assemblyProgress: null })
  },
  reset: () => set({ session: null, loadState: 'idle', error: null, quotaGate: null, approvalResult: null, assemblyProgress: null }),
}))
